import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Alert from '@mui/material/Alert';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Checkbox from '@mui/material/Checkbox';
import Card from '@mui/material/Card';
import { DataGrid } from '@mui/x-data-grid';

import { selectSitter, fetchPendingJobs } from './action';
import { selectPendingJobs, selectUpdateJobError } from './selector';

const JobApprovalList = () => {
  const dispatch = useDispatch();
  const jobApprovals = useSelector(selectPendingJobs);
  const updateJobError = useSelector(selectUpdateJobError);

  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedSitter, setSelectedSitter] = useState([]);
  const [sitterSelectionError, setSitterSelectionError] = useState(null);

  useEffect(() => {
    dispatch(fetchPendingJobs());
  }, [dispatch]);

  const petTypes = [
    { label: 'Cat', value: 'cat' },
    { label: 'Dog', value: 'dog' },
  ];

  const onPlaceBid = (row) => {
    setSelectedJob(row);
  };

  const handleCloseDialog = () => {
    setSelectedJob(null);
  };

  const handleApproveJob = () => {
    if (selectedSitter.length !== 1) {
      setSitterSelectionError('You must select one sitter');
      return;
    }
    dispatch(selectSitter(selectedJob, selectedSitter));
    setSelectedSitter([]);
    setSelectedJob(null);
    setSitterSelectionError(null);
  };

  const columns = [
    { field: '_id', sortable: false, headerName: 'Job ID', width: 220 },
    {
      field: 'createdAt',
      headerName: 'Created on',
      width: 150,
      valueGetter: (params) => {
        const date = new Date(params.value);
        return date.toLocaleDateString('en-US');
      }
    },
    {
      field: 'petName',
      headerName: 'Pet Name',
      width: 150,
      valueGetter: (params) => params.row.jobDetails.petName,
    },
    {
      field: 'petType',
      headerName: 'Pet Type',
      width: 150,
      valueGetter: (params) => {
        const { petType } = params.row.jobDetails;
        return petTypes.find(pet => pet.value === petType)?.label;
      },
    },
    {
      field: 'sitterBids',
      headerName: 'Bids',
      width: 120,
      valueGetter: (params) => params.value.length,
    },
    {
      field: 'action',
      headerName: 'Action',
      width: 150,
      isableClickEventBubbling: true,
      sortable: false,
      renderCell: (params) => (
        params.row.sitterBids.length === 0 ? (
          <Button variant="text" disabled>No Bids</Button>
        ) : (
          <Button variant="text" onClick={() => onPlaceBid(params.row)}>
            Accept Bid
          </Button>
        )
      ),
    },
  ];

  const handleSitterSelect = (event) => {
    if (event.target.checked) {
      setSelectedSitter([event.target.name]);
    } else {
      setSelectedSitter([]);
    }
  }

  return (
    <Container component="main" maxWidth="lg" sx={{ pt: 6 }}>
        <Card sx={{ padding: "2rem" }}>
      <Dialog
        maxWidth="xs"
        open={selectedJob !== null}
      >
        <DialogTitle sx={{ pb: 3.5 }}>Select a sitter</DialogTitle>
        <DialogContent>
          <FormControl
            required
            error={sitterSelectionError !== null}
            component="fieldset"
            sx={{ my: 2 }}
            variant="standard"
          >
            <FormLabel component="legend">Pick a sitter</FormLabel>
            <FormGroup>
              {selectedJob?.sitterBids.map((sitterId, idx) => (
                <FormControlLabel
                  key={`${sitterId}${idx}`}
                  control={
                    <Checkbox
                      name={sitterId}
                      checked={selectedSitter.includes(sitterId)}
                      onChange={handleSitterSelect}
                    />
                  }
                  label={sitterId}
                  sx={{ mx: 1, mt: 1 }}
                />
              ))}
            </FormGroup>
          </FormControl>
          <FormHelperText>{sitterSelectionError}</FormHelperText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button autoFocus onClick={handleCloseDialog}>No</Button>
          <Button onClick={handleApproveJob}>Yes</Button>
        </DialogActions>
      </Dialog>
      <Typography variant="h5" component="h1">
        Job To Approve: {jobApprovals.length}
      </Typography>
      {updateJobError && (
        <Alert severity="error" sx={{ mt: 1 }}>{updateJobError}</Alert>
      )}
      <div
        style={{
          height: 80 * jobApprovals.length + 200,
          width: '100%',
          marginTop: 20,
        }}
      >
        <DataGrid
          rows={jobApprovals}
          columns={columns}
          getRowId={row => row._id}
          pageSize={10}
          rowsPerPageOptions={[10]}
        />
      </div>
      </Card>
    </Container>
  );
};

export default JobApprovalList;
